/**
 * The closed vocabularies of the meeting note taker (#13646 N1, #13699 N3).
 *
 * `Recording.mode`, `Recording.status`, `ExtractedItem.kind`,
 * `ExtractedItem.state` and `TranscriptionJob.status` are plain `String`
 * columns in `prisma/schema.prisma`. This module is what closes them.
 *
 * ## Why these are not Prisma/Postgres enums
 *
 * The plan artifact behind #13646 specified four Prisma enums. The repo
 * default moved the other way first: since #13444 an enumerated vocabulary
 * lives in code, not in the database (AGENTS.md; the argument in full in
 * `src/lib/strategic-plans/vocabularies.ts`, and the same call #13574 made
 * for `FeedbackAnchorKind`). A Postgres enum cannot have a value removed at
 * all, and a value added in one transaction cannot be used in that same
 * transaction — so every addition, rename and retirement costs a migration.
 *
 * `status` is the vocabulary that settles it. It is a pipeline state machine
 * whose stages are being built one child issue at a time (N2 capture and
 * upload, N3 transcription, N4 extraction, N5 conversion); a value set that
 * is expected to move while the feature is being assembled is exactly the
 * case the default exists for. Repo convention outranks the issue's wording;
 * this comment is the record of the choice.
 *
 * WHAT STILL ENFORCES THE VALUES: the zod schemas below, at every
 * boundary that accepts one. The database no longer rejects a bad value, so
 * a write path that skips them will persist garbage. Parse at the edge; never
 * hand a bare `string` to Prisma.
 *
 * ## Client-safe
 *
 * zod only — no Prisma import, no `server-only`. The capture surface (N2)
 * builds these payloads in the browser and must validate them against the
 * same vocabulary the server applies.
 */

import { z } from 'zod'

/**
 * What was captured, and HOW the bytes got here.
 *
 *   MEETING — a live capture. A browser held the microphone, and every chunk
 *             was produced by a `MediaRecorder` in this product.  (N2 #13698)
 *   UPLOAD  — audio captured somewhere else: a phone on the table, a room
 *             system, a handheld recorder. The same `Recording` row, the same
 *             storage prefix and the same transcription job — the entry point
 *             is the only thing that differs.                     (#13806)
 *   IMPORTED — a transcript ANOTHER PRODUCT already made (Granola today),
 *             written already `TRANSCRIBED` with its segments. No audio
 *             (`storageKey` is null), no chunks, no `TranscriptionJob`.
 *             Written only by `importExternalNote`
 *             (`@/lib/notes/import-note`).        (#14412, ADR-0115 § 5)
 *
 * `UPLOAD` is the column paying off exactly as its #13646 docblock predicted:
 * the mode is stored from the start so a later reader never has to INFER it,
 * and adding one costs no migration because the column is a plain `String`
 * closed by the schema below rather than a Postgres enum.
 *
 * ## It is not a cosmetic label — three reads turn on it
 *
 * An uploaded recording must never be offered back as a live capture to
 * RESUME (that would open a microphone and append a room's audio to somebody's
 * phone recording, in a container that cannot even concatenate), must never be
 * ADOPTED as the meeting an in-flight capture belongs to, and must never be
 * counted as the capture the bar is running. All three go through
 * `inFlightCaptureWhere` (`./capture-note`), which filters on
 * {@link LIVE_CAPTURE_RECORDING_MODE} for that reason.
 */
export const RECORDING_MODES = ['MEETING', 'UPLOAD', 'IMPORTED'] as const

export const recordingModeSchema = z.enum(RECORDING_MODES)

export type RecordingMode = (typeof RECORDING_MODES)[number]

export const DEFAULT_RECORDING_MODE: CaptureRecordingMode = 'MEETING'

/**
 * The mode a LIVE capture is written in — the same value as the default, named
 * separately because the two are different claims.
 *
 * `DEFAULT_RECORDING_MODE` answers "what does a writer put here when nobody
 * said"; this answers "which rows is a microphone allowed to append to". They
 * coincide today. A future non-meeting LIVE mode (a walkthrough, a one-on-one
 * debrief — the growth the column was added for) would change the first
 * without changing the second, and a single constant doing both jobs is how a
 * resume path silently starts offering the wrong rows.
 */
export const LIVE_CAPTURE_RECORDING_MODE: CaptureRecordingMode = 'MEETING'

/** The mode a file uploaded from elsewhere is written in (#13806). */
export const UPLOADED_RECORDING_MODE: CaptureRecordingMode = 'UPLOAD'

/**
 * The mode an imported note's recording is written in (#14412, ADR-0115 § 5).
 *
 * Every reader that assumed a recording has audio must check for this one:
 * `storageKey` is null and `mimeType` names the source's content type, not
 * an audio type.
 */
export const IMPORTED_RECORDING_MODE: RecordingMode = 'IMPORTED'

/**
 * The modes the CAPTURE writer (`startRecording`) may open a row in — every
 * mode that brings bytes this product stores.
 *
 * `IMPORTED` is absent on purpose. `startRecording` is a `'use server'`
 * export, so its mode is client input; parsing it through
 * {@link recordingModeSchema} would let a browser open an `IMPORTED` row with
 * a consent stamp and no import provenance. Imports are written by
 * `importExternalNote` alone.
 */
export const CAPTURE_RECORDING_MODES = [
  'MEETING',
  'UPLOAD',
] as const satisfies readonly RecordingMode[]

export const captureRecordingModeSchema = z.enum(CAPTURE_RECORDING_MODES)

export type CaptureRecordingMode = (typeof CAPTURE_RECORDING_MODES)[number]

/**
 * Which external product an `IMPORTED` recording came from
 * (`Recording.importSource`, #14412). A plain `String` column closed here, per
 * ADR-0107. One value per importer that ships; Granola's sync is #14413.
 */
export const IMPORT_SOURCES = ['granola'] as const

export const importSourceSchema = z.enum(IMPORT_SOURCES)

export type ImportSource = (typeof IMPORT_SOURCES)[number]

/**
 * Which sources a LIVE capture recorded (`Recording.captureSource`, #14545):
 * the microphone alone, or the microphone mixed with computer audio shared
 * through `getDisplayMedia` (a tab, or the whole system where the browser
 * allows it). A plain `String?` column closed here, per ADR-0107. Written at
 * start and rewritten when computer audio is added or lost partway through,
 * so it says what the capture was recording LAST; NULL on uploads, imports
 * and every row from before the column.
 */
export const CAPTURE_SOURCES = [
  'microphone',
  'microphone_and_computer',
] as const

export const captureSourceSchema = z.enum(CAPTURE_SOURCES)

export type CaptureSource = (typeof CAPTURE_SOURCES)[number]

/** A microphone-only capture — and what an unrecognised claim reads as. */
export const MICROPHONE_CAPTURE_SOURCE: CaptureSource = 'microphone'

/**
 * Narrow a client's claim. Anything outside {@link CAPTURE_SOURCES} —
 * including nothing at all, from a bundle that predates the toggle — is
 * `microphone`, the only thing such a bundle could have recorded.
 */
export function toCaptureSource(value: unknown): CaptureSource {
  const parsed = captureSourceSchema.safeParse(value)
  return parsed.success ? parsed.data : MICROPHONE_CAPTURE_SOURCE
}

/**
 * What `Recording.mimeType` (NOT NULL) holds for an import: the content type
 * of what the source product returned, never an audio type (ADR-0115 § 5).
 */
export const IMPORT_SOURCE_CONTENT_TYPES: Record<ImportSource, string> = {
  granola: 'text/markdown',
}

/**
 * The capture/processing lifecycle, in the order a recording moves through
 * it. One stage per child issue that writes it, so the set is a map of what
 * is built and what is not:
 *
 *   CAPTURING    — the browser is recording. No object exists yet.  (N2)
 *   UPLOADING    — chunks are in flight to Tigris.                  (N2)
 *   UPLOADED     — `storageKey` is set; nothing is derived yet.     (N2)
 *   TRANSCRIBING — handed to the speech-to-text provider.           (N3)
 *   TRANSCRIBED  — `TranscriptSegment` rows are complete.           (N3)
 *   EXTRACTING   — deriving `ExtractedItem` rows from the segments. (N4)
 *   READY        — items are reviewable and convertible to tasks.   (N5)
 *   FAILED       — a terminal stage failed; the row is kept so the
 *                  operator can see what happened rather than having the
 *                  recording silently disappear.
 *
 * The order is documentation, not a ranking anything computes on today, and
 * the set is NOT a state machine anything enforces yet — N2 is the first
 * writer and owns the transitions.
 */
export const RECORDING_STATUSES = [
  'CAPTURING',
  'UPLOADING',
  'UPLOADED',
  'TRANSCRIBING',
  'TRANSCRIBED',
  'EXTRACTING',
  'READY',
  'FAILED',
] as const

export const recordingStatusSchema = z.enum(RECORDING_STATUSES)

export type RecordingStatus = (typeof RECORDING_STATUSES)[number]

/** Where a recording starts. Not a column default — N2 sets it explicitly. */
export const DEFAULT_RECORDING_STATUS: RecordingStatus = 'CAPTURING'

/**
 * What was extracted from the transcript.
 *
 *   ACTION   — a quoted commitment somebody made.
 *   DECISION — a quoted decision the group reached.
 *
 * Both are LITERAL quotes from the transcript (`ExtractedItem.quotedText`).
 * There is deliberately no kind for a generated recommendation, and no
 * `rationale`/`suggestedNextStep` column for one to live in: a machine
 * recommendation stored beside a meeting record risks construal as a
 * district obligation under public-records law.
 */
export const EXTRACTED_ITEM_KINDS = ['ACTION', 'DECISION'] as const

export const extractedItemKindSchema = z.enum(EXTRACTED_ITEM_KINDS)

export type ExtractedItemKind = (typeof EXTRACTED_ITEM_KINDS)[number]

/**
 * Where an extracted item sits in review.
 *
 *   PROPOSED  — extracted, nobody has looked at it. The column default.
 *   ACCEPTED  — a human kept it; N5 may convert it into a `Task`.
 *   DISMISSED — a human rejected it, with `dismissedReason` saying why.
 *
 * Nothing is ever created ACCEPTED: an item only leaves PROPOSED by a human
 * acting on it, which is the whole reason the extraction is a proposal and
 * not a write.
 */
export const EXTRACTED_ITEM_STATES = [
  'PROPOSED',
  'ACCEPTED',
  'DISMISSED',
] as const

export const extractedItemStateSchema = z.enum(EXTRACTED_ITEM_STATES)

export type ExtractedItemState = (typeof EXTRACTED_ITEM_STATES)[number]

/**
 * The state every extracted item is born in.
 *
 * `ExtractedItem.state` carries `@default("PROPOSED")` in the schema, and
 * `tests/unit/prisma/notetaker-schema-13646.test.ts` pins the two to each
 * other — a column default and a code default that disagree is the failure
 * mode this constant exists to make impossible.
 */
export const DEFAULT_EXTRACTED_ITEM_STATE: ExtractedItemState = 'PROPOSED'

/**
 * Where a transcription job sits in its lifecycle (#13699, N3).
 *
 *   PENDING   — enqueued, nobody has claimed it. The state a job is born in
 *               and the state a retryable failure returns it to.
 *   RUNNING   — claimed by exactly one runner, which is advancing it.
 *   SUCCEEDED — the provider finished and every segment it produced is
 *               written.
 *   FAILED    — terminal. The segments already produced STAY; the row keeps
 *               `lastError` so a human can read what happened.
 *
 * A FIFTH string column, closed here for the same reasons as the four above
 * and one more that is specific to it: the retry/claim machinery reads and
 * writes this value from a cron route, and a Postgres enum cannot have a
 * value used in the transaction that adds it — which is precisely the shape
 * a "add a PAUSED state" follow-up would take.
 *
 * There is deliberately no CLAIMED-distinct-from-RUNNING and no CANCELLED:
 * the claim IS the transition to RUNNING (a conditional update on PENDING is
 * what makes two runners unable to take the same row), and nothing in this
 * issue can cancel a job.
 */
export const TRANSCRIPTION_JOB_STATUSES = [
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
] as const

export const transcriptionJobStatusSchema = z.enum(TRANSCRIPTION_JOB_STATUSES)

export type TranscriptionJobStatus = (typeof TRANSCRIPTION_JOB_STATUSES)[number]

/**
 * Where a transcription job starts.
 *
 * `transcription_jobs.status` carries NO column default — the enqueue path
 * writes this constant explicitly, so the value a row is born with is
 * decided by code that can be read rather than by DDL that cannot be
 * changed without a migration.
 */
export const DEFAULT_TRANSCRIPTION_JOB_STATUS: TranscriptionJobStatus =
  'PENDING'

/**
 * The statuses from which no further `TranscriptSegment` can arrive.
 *
 * FAILED belongs here beside SUCCEEDED. A terminally failed job keeps the
 * segments it already committed — "partial beats nothing" is the rule the
 * whole write path is built on — and it is FINISHED, so the partial
 * transcript it left is the whole transcript there will ever be. Treating it
 * as still-in-flight would park a meeting's extraction forever.
 */
export const TERMINAL_TRANSCRIPTION_JOB_STATUSES: readonly TranscriptionJobStatus[] =
  ['SUCCEEDED', 'FAILED']

/**
 * True when transcription for this job is over, whichever way it ended.
 *
 * Takes a plain `string` because `TranscriptionJob.status` is a `String`
 * column by the #13444 convention, so a reader holds whatever is in the row
 * rather than a narrowed union. An unrecognised value answers false —
 * "assume more is coming" is the safe direction, since the cost is a pass
 * that waits rather than one that extracts over a prefix (#14059).
 */
export function transcriptionIsFinished(status: string): boolean {
  return (TERMINAL_TRANSCRIPTION_JOB_STATUSES as readonly string[]).includes(
    status,
  )
}

/**
 * How firmly an extracted item was said (#14376).
 *
 *   proposed — somebody suggested it; the quoted words carry no explicit
 *              agreement. Also the level a hedge ("probably", "if the vendor
 *              comes back") caps everything at.
 *   decided  — the quoted words themselves carry decision language: "we'll
 *              go with", "agreed", "decided", "let's do".
 *   assigned — an ACTION whose quoted words name the person taking it on or
 *              being given it: "Maria will send it", "Maria, can you…".
 *
 * Ordered weakest to firmest. Plain discussion has no level because it is
 * not an item at all: extraction already requires an action or a decision.
 *
 * The level the model claims is a REQUEST, not the stored value. The fold's
 * guards (`./extract-items-fold.ts`, cues in `./commitment-cues.ts`) check
 * it against the located quote and downgrade anything the words do not
 * support — the two failures this exists to prevent are a hedge made
 * certain and a discussion presented as a decision.
 *
 * A `String?` column on `ExtractedItem` and `Evidence`, closed here, for the
 * same ADR-0107 reasons as every vocabulary above. NULL on rows extracted
 * before #14376, which render with no level.
 */
export const COMMITMENT_LEVELS = ['proposed', 'decided', 'assigned'] as const

export const commitmentLevelSchema = z.enum(COMMITMENT_LEVELS)

export type CommitmentLevel = (typeof COMMITMENT_LEVELS)[number]

/**
 * The level an item gets when nothing in its quote supports a firmer one —
 * including when the model sent no level at all.
 */
export const DEFAULT_COMMITMENT_LEVEL: CommitmentLevel = 'proposed'

/**
 * A stored level read back, or null for anything outside the vocabulary.
 *
 * The column is a plain `String?`, so the database accepts garbage; a reader
 * that renders an unknown word as a label would be showing a claim nobody
 * made. Null renders nothing, which is what a pre-#14376 row does too.
 */
export function parseCommitmentLevel(value: unknown): CommitmentLevel | null {
  const parsed = commitmentLevelSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}
